"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CircuitBreaker = void 0;
var helpers_1 = require("./helpers");
var types_1 = require("./types");
var CircuitBreaker = /** @class */ (function () {
    function CircuitBreaker(config) {
        this.state = types_1.CircuitBreakerState.CLOSED;
        /**
         * Массив логов запросов. Хранит в себе {minimumSamples} последних запросов.
         * Время хранения запроса {samplingDuration}
         */
        this.samples = [];
        this.lastHalfOpenTaskIndex = 0;
        this.config = Object.assign({}, config);
    }
    CircuitBreaker.prototype.setConfig = function (nextConfig) {
        this.config = Object.assign(this.config, nextConfig);
    };
    /**
     * Метод, который в зависимости от состояния говорит, можно ли запускать задачу.
     *
     * Если состояние OPEN:
     * - Проверяем истекло ли время {openDuration}
     * - Если время истекло, то переводим в состояние HALF_OPEN разрешаем выполнить одну(!) задачу
     * - Если время не истекло, то запрещаем выполнять задачу
     *
     * Если состояния HALF_OPEN:
     * - Проверяем истекло ли время пробной задачи
     * - Если нет, то запрещаем выполнение следующей задачи
     * - Если да, то пытаемся закрыть CB
     *
     * Если состояние CLOSE:
     * - Анализируем колекцию сэмплов
     * - Если допустимый процент неудачных задач превышен, то "открываем" CB и запрещаем выполнение задачи
     * - Если попрог не превышен, то позволяем выполнить задачу
     */
    CircuitBreaker.prototype.canRunTask = function () {
        var _a = this.config, maxTimeForHalfOpenRequest = _a.maxTimeForHalfOpenRequest, openDuration = _a.openDuration;
        if (this.state === types_1.CircuitBreakerState.OPEN) {
            if (!(this.lastCBOpenTime == null) && (0, helpers_1.isTimeExpired)(this.lastCBOpenTime, openDuration)) {
                this.halfOpen();
                return true;
            }
            return false;
        }
        if (this.state === types_1.CircuitBreakerState.HALF_OPEN) {
            // С начало смотрим, выполнилась ли наша задача. Если нет, то смотрим, истекло ли время на задачу.
            if (this.analyzeLastHalfOpenTask() ||
                (this.lastHalfOpenTime && (0, helpers_1.isTimeExpired)(this.lastHalfOpenTime, maxTimeForHalfOpenRequest))) {
                return this.tryCloseCircuitBreaker();
            }
            return false;
        }
        var failureThresholdExceeded = this.failureThresholdExceeded();
        if (failureThresholdExceeded) {
            this.open();
            return false;
        }
        return true;
    };
    CircuitBreaker.prototype.recordSuccess = function () {
        this.record(true);
    };
    CircuitBreaker.prototype.recordFailure = function () {
        this.record(false);
    };
    CircuitBreaker.prototype.setState = function (state) {
        this.state = state;
        return state;
    };
    /**
     * Переводим CB в состояние полуоткрыт. Это позволяет нам отправить 1 запрос.
     */
    CircuitBreaker.prototype.halfOpen = function () {
        this.lastHalfOpenTime = Date.now();
        this.setState(types_1.CircuitBreakerState.HALF_OPEN);
    };
    /**
     * Открытие предохранителя.
     * Когда мы открываем предохранитель, то очищаем время последнего полуоткрытого состояния.
     */
    CircuitBreaker.prototype.open = function () {
        // Очищаем коллекцию сэмплов, потому что при открытом состоянии мы не собираем результаты задач
        // и это нам гарантирует, что когда мы перейдем в состояние "полу открыт", у нас будет только 1 задача
        this.clearSamples();
        this.lastHalfOpenTime = undefined;
        this.lastCBOpenTime = Date.now();
        this.setState(types_1.CircuitBreakerState.OPEN);
    };
    /**
     * Закрытие CB. Когда мы закрываем CB, то мы должны очистить все таймеры.
     */
    CircuitBreaker.prototype.close = function () {
        this.lastCBOpenTime = undefined;
        this.lastHalfOpenTime = undefined;
        this.setState(types_1.CircuitBreakerState.CLOSED);
    };
    /**
     * Попытка закрыть CB из состояния HALF_OPEN. Анализируем последнюю задачу:
     * - Если она успешна, то закрываем, если нет то открываем.
     */
    CircuitBreaker.prototype.tryCloseCircuitBreaker = function () {
        if (this.analyzeLastHalfOpenTask()) {
            this.close();
            return true;
        }
        this.open();
        return false;
    };
    CircuitBreaker.prototype.analyzeLastHalfOpenTask = function () {
        var _a;
        if (!this.samples.length) {
            return false;
        }
        return ((_a = this.samples[this.lastHalfOpenTaskIndex]) === null || _a === void 0 ? void 0 : _a.success) || false;
    };
    CircuitBreaker.prototype.record = function (success) {
        var maximumSamples = this.config.maximumSamples;
        this.clearExpiredSamples();
        if (this.samples.length >= maximumSamples) {
            this.samples = this.samples.slice(1);
        }
        this.samples.push({
            date: Date.now(),
            success: success,
        });
    };
    /**
     * Проверяем, превышен ли порог допустимых фэйлов
     */
    CircuitBreaker.prototype.failureThresholdExceeded = function () {
        if (!this.samples.length || this.samples.length < this.config.minimumSamples) {
            return false;
        }
        var failureTreshold = this.config.failureTreshold;
        var allSamples = this.samples.length;
        // Количество сфэйленных запросов
        var failureSamples = this.samples.reduce(function (count, _a) {
            var success = _a.success;
            return (!success ? count + 1 : count);
        }, 0);
        // Сравниваем процент сфэйленных запросов с допустимым количеством
        return failureSamples / allSamples >= failureTreshold;
    };
    CircuitBreaker.prototype.clearExpiredSamples = function () {
        if (!this.samples.length) {
            return;
        }
        var samplingDuration = this.config.samplingDuration;
        this.samples = this.samples.filter(function (_a) {
            var date = _a.date;
            return !(0, helpers_1.isTimeExpired)(date, samplingDuration);
        });
    };
    CircuitBreaker.prototype.clearSamples = function () {
        this.samples = [];
    };
    return CircuitBreaker;
}());
exports.CircuitBreaker = CircuitBreaker;
//# sourceMappingURL=circuitBreaker.js.map